import * as React from "react";
import Map, {
  FullscreenControl,
  ScaleControl,
  Popup,
  Source,
  Layer,
} from "react-map-gl/mapbox";
import { Button, Box, alpha } from "@mui/material";
import { ChevronRight } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { CONFIG } from "../../config";
import { getMapStyle } from "../../utils/getMapStyle";

import { useNavigate } from "react-router-dom";
import { useMapControls } from "../../hooks/useMapControls";
import mapboxgl from "mapbox-gl";
import PitchToggle from "./utils/PitchToggle";
import { withMapErrorBoundary } from "../ErrorBoundaries";
import { useTheme } from "@mui/system";
import { useDevices } from "../../hooks/queries/useDevices";
import { useGeofences } from "../../hooks/queries/useGeofences";
import { useTranslation } from "react-i18next";
import OptimizedNavigationControl from "./utils/OptimizedNavigationControl";
import { usePerformanceOptimizations } from "../../hooks/usePerformanceOptimizations";
import { VirtualizedDeviceMarkers } from "./components/VirtualizedDeviceMarkers";
import { logger } from "../../utils/logger";
import { createEnhancedPopupContent } from "./components/MapPopup";
import { isDeviceOutdatedByTime } from "../../utils/deviceStatus";
import {
  getDeviceFreshnessTime,
  getBatteryMillivolts,
  getDeviceLastData,
} from "../../utils/deviceUtils";
import { parseJsonSafe } from "../../utils/json";

mapboxgl.accessToken = CONFIG.mapboxAccessToken;

const CLUSTER_THRESHOLD = 500; // Only cluster if more than this many devices

const getMapboxMap = (mapRef) => {
  const current = mapRef?.current;
  if (!current) {
    return null;
  }

  return typeof current.getMap === "function" ? current.getMap() : current;
};

const isValidCoordinate = (lng, lat) => {
  if (lng == null || lat == null) return false;
  const lngNum = Number(lng);
  const latNum = Number(lat);
  if (!Number.isFinite(lngNum) || !Number.isFinite(latNum)) return false;
  if (lngNum === 0 || latNum === 0) return false;
  if (lngNum < -180 || lngNum > 180) return false;
  if (latNum < -90 || latNum > 90) return false;
  return true;
};

// Memoized DevicesMap component to prevent unnecessary re-renders
const DevicesMap = React.memo(({ devices: propDevices }) => {
  // Redux state (for UI state)
  const organisation = useSelector((state) => state.selectedOrganisation);
  const pitch = useSelector((state) => state.pitch);

  // TanStack Query data - use prop devices if provided, otherwise fetch
  const { data: fetchedDevices = [] } = useDevices(organisation?.id);
  const devices = propDevices ?? fetchedDevices;
  const { data: geofences = [] } = useGeofences(organisation?.id);

  const navigate = useNavigate();
  const theme = useTheme();

  const { t } = useTranslation("common");

  // Performance optimizations
  const { debounce, throttleRAF, cleanup } = usePerformanceOptimizations();

  const mapRef = React.useRef(null);
  const optimizedNavControlRef = React.useRef(null);
  const pitchToggleControlRef = React.useRef(null);
  const popupCloseHandlerRef = React.useRef(null);
  const [mapLoaded, setMapLoaded] = React.useState(false);
  const [showPopup, setShowPopup] = React.useState(false);
  const [selectedDevice, setSelectedDevice] = React.useState(null);

  // Cleanup performance optimizations on unmount
  React.useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const { filteredDevices, fitToHistory, initialViewState } = useMapControls(
    devices,
    organisation,
    mapRef,
    pitch
  );

  const handleMapLoad = React.useCallback(
    (e) => {
      mapRef.current = e.target;
      setMapLoaded(true);
      const map = getMapboxMap(mapRef);

      if (!map) {
        return;
      }

      // Add optimized navigation control with passive event listeners
      if (!optimizedNavControlRef.current) {
        optimizedNavControlRef.current = new OptimizedNavigationControl();
        map.addControl(optimizedNavControlRef.current, "top-left");
      }

      // Debounced map operations for better performance
      const debouncedMapSetup = debounce(
        () => {
          if (
            organisation.hero === "map" &&
            organisation.centerandzoom !== "false"
          ) {
            map.flyTo({
              center: organisation.center.split(",").map(Number),
              zoom: organisation.zoom,
              animate: false,
              pitch: pitch,
            });
          } else {
            fitToHistory();
          }
        },
        100,
        "mapSetup"
      );

      debouncedMapSetup();

      if (!pitchToggleControlRef.current) {
        pitchToggleControlRef.current = new PitchToggle({ pitch });
      }
      if (!map.hasControl(pitchToggleControlRef.current)) {
        map.addControl(pitchToggleControlRef.current, "top-left");
      }

      // Debounced pitch setting
      const debouncedPitchSet = debounce(
        () => {
          if (typeof pitch === "number") {
            map.setPitch(pitch);
          }
        },
        150,
        "pitchSet"
      );

      debouncedPitchSet();

      // Throttled popup close handler for better responsiveness
      const throttledPopupClose = throttleRAF(() => {
        setShowPopup(false);
      }, "popupClose");

      if (popupCloseHandlerRef.current) {
        map.off("click", popupCloseHandlerRef.current);
      }
      popupCloseHandlerRef.current = throttledPopupClose;
      map.on("click", throttledPopupClose);
    },
    [organisation, fitToHistory, pitch, debounce, throttleRAF]
  );

  React.useEffect(() => {
    return () => {
      const map = getMapboxMap(mapRef);
      if (map && popupCloseHandlerRef.current) {
        map.off("click", popupCloseHandlerRef.current);
      }
      [optimizedNavControlRef.current, pitchToggleControlRef.current].forEach(
        (control) => {
          if (map && control && map.hasControl(control)) {
            map.removeControl(control);
          }
        }
      );
      setMapLoaded(false);
      popupCloseHandlerRef.current = null;
    };
  }, []);

  const flyToDevice = React.useCallback(
    (device) => {
      if (!isValidCoordinate(device?.lng, device?.lat)) {
        return;
      }

      // Debounced flyTo operation for smoother performance
      const debouncedFlyTo = debounce(
        () => {
          const lng = Number(device.lng);
          const lat = Number(device.lat);
          const map = getMapboxMap(mapRef);

          if (!map) {
            return;
          }

          map.flyTo({
            center: [lng, lat],
            zoom: 15,
            animate: true,
            pitch: pitch,
            duration: 1000, // Reduced from default 2000ms for faster response
          });

          // Throttled pitch setting
          const throttledPitchSet = throttleRAF(() => {
            if (typeof pitch === "number") {
              map.setPitch(pitch);
            }
          }, "flyToPitch");

          setTimeout(throttledPitchSet, 100);

          setSelectedDevice(device);
          setShowPopup(true);
        },
        50,
        "flyToDevice"
      );

      debouncedFlyTo();
    },
    [pitch, debounce, throttleRAF]
  );

  const isDeviceOutdated = React.useCallback(
    (freshnessTime) => isDeviceOutdatedByTime(freshnessTime),
    []
  );

  const popupContent = React.useMemo(() => {
    if (!selectedDevice) {
      return "";
    }

    const parsedLastData = getDeviceLastData(selectedDevice);
    const fallbackLastData =
      (selectedDevice.lastData && typeof selectedDevice.lastData === "object"
        ? selectedDevice.lastData
        : null) ??
      (selectedDevice.latest_data &&
      typeof selectedDevice.latest_data === "object"
        ? selectedDevice.latest_data
        : null);

    const lastData = parsedLastData ?? fallbackLastData;

    const rawSource =
      selectedDevice.last_data ??
      selectedDevice.lastData ??
      selectedDevice.latest_data ??
      null;

    const rawData =
      typeof rawSource === "string"
        ? rawSource
        : JSON.stringify(rawSource || {});

    const batteryMv = getBatteryMillivolts(lastData, selectedDevice, null);

    const freshnessReference = getDeviceFreshnessTime(selectedDevice);

    const statusColor =
      freshnessReference && isDeviceOutdated(freshnessReference)
        ? theme.palette.error.main
        : theme.palette.success.main;

    return createEnhancedPopupContent({
      title:
        (selectedDevice.name && selectedDevice.name.trim()) ||
        t("common.device", { defaultValue: "Device" }),
      timeCreated: freshnessReference || new Date().toISOString(),
      geocode: selectedDevice.last_location,
      battery: batteryMv,
      deviceBrand: selectedDevice.device_brand,
      rawData,
      latitude: selectedDevice.lat ? Number(selectedDevice.lat) : undefined,
      longitude: selectedDevice.lng ? Number(selectedDevice.lng) : undefined,
      primaryColor: theme.palette.primary.main,
      isDarkMode: theme.palette.mode === "dark",
      statusColor,
      showSensorGrid: false,
      showCoordinates: false,
    });
  }, [
    selectedDevice,
    theme.palette.primary.main,
    theme.palette.mode,
    theme.palette.error.main,
    theme.palette.success.main,
    isDeviceOutdated,
    t,
  ]);

  // Memoized geofence processing for better performance
  const geofenceFeatures = React.useMemo(() => {
    if (!geofences || !geofences.length) return [];

    const features = geofences
      .filter((geofence) => geofence.polygon_data) // Only include geofences with polygon data
      .reduce((acc, geofence) => {
        try {
          let polygonData = geofence.polygon_data;

          // If polygon_data is a string, parse it
          if (typeof polygonData === "string") {
            polygonData = parseJsonSafe(polygonData, null);
            if (!polygonData) {
              return acc;
            }
          }

          // Handle different geofence formats
          if (polygonData.type === "FeatureCollection") {
            // Return all features with geofence metadata
            acc.push(
              ...polygonData.features.map((feature) => ({
                ...feature,
                properties: {
                  ...feature.properties,
                  geofence_id: geofence.id,
                  geofence_name: geofence.name,
                  geofence_status: geofence.status,
                },
              }))
            );
          } else if (polygonData.type === "Feature") {
            // Single feature
            acc.push({
              ...polygonData,
              properties: {
                ...polygonData.properties,
                geofence_id: geofence.id,
                geofence_name: geofence.name,
                geofence_status: geofence.status,
              },
            });
          } else if (
            polygonData.type === "Polygon" ||
            polygonData.type === "MultiPolygon"
          ) {
            // Raw geometry
            acc.push({
              type: "Feature",
              geometry: polygonData,
              properties: {
                geofence_id: geofence.id,
                geofence_name: geofence.name,
                geofence_status: geofence.status,
              },
            });
          }
        } catch (error) {
          logger.error("Error processing geofence:", geofence.id, error);
        }
        return acc;
      }, []);

    return features;
  }, [geofences]);

  // Create geofence GeoJSON source
  const geofenceSource = React.useMemo(
    () => ({
      type: "FeatureCollection",
      features: geofenceFeatures,
    }),
    [geofenceFeatures]
  );

  // Generate pin features for geofences (center points)
  const geofencePinFeatures = React.useMemo(() => {
    return geofenceFeatures
      .map((feature) => {
        try {
          // Calculate centroid for pin placement
          let centroid;
          if (feature.geometry.type === "Polygon") {
            // Simple centroid calculation for polygon
            const coords = feature.geometry.coordinates[0];
            const x =
              coords.reduce((sum, coord) => sum + coord[0], 0) / coords.length;
            const y =
              coords.reduce((sum, coord) => sum + coord[1], 0) / coords.length;
            centroid = [x, y];
          } else if (feature.geometry.type === "MultiPolygon") {
            // Use first polygon's centroid for MultiPolygon
            const coords = feature.geometry.coordinates[0][0];
            const x =
              coords.reduce((sum, coord) => sum + coord[0], 0) / coords.length;
            const y =
              coords.reduce((sum, coord) => sum + coord[1], 0) / coords.length;
            centroid = [x, y];
          } else {
            return null;
          }

          return {
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: centroid,
            },
            properties: feature.properties,
          };
        } catch (error) {
          logger.error(
            "Error creating pin for geofence:",
            feature.properties?.geofence_id,
            error
          );
          return null;
        }
      })
      .filter(Boolean);
  }, [geofenceFeatures]);

  // Create geofence pins GeoJSON source
  const geofencePinSource = React.useMemo(
    () => ({
      type: "FeatureCollection",
      features: geofencePinFeatures,
    }),
    [geofencePinFeatures]
  );

  // Memoized map style to prevent re-renders
  const mapStyle = React.useMemo(
    () =>
      getMapStyle(theme.palette.mode, {
        light: "mapbox://styles/bfgneil/cm6gclmw8003501s31vcx94z8",
        dark: "mapbox://styles/bfgneil/cm6gcmt5l00ck01r56spcffre",
      }),
    [theme.palette.mode]
  );

  // Memoized layer paint properties
  const geofenceFillPaint = React.useMemo(
    () => ({
      "fill-color": [
        "case",
        ["==", ["get", "geofence_status"], "active"],
        theme.palette.mode === "dark" ? "#4CAF50" : "#4CAF50", // Green for active
        theme.palette.mode === "dark" ? "#757575" : "#9E9E9E", // Gray for inactive
      ],
      "fill-opacity": 0.2,
    }),
    [theme.palette.mode]
  );

  const geofenceStrokePaint = React.useMemo(
    () => ({
      "line-color": [
        "case",
        ["==", ["get", "geofence_status"], "active"],
        theme.palette.mode === "dark" ? "#4CAF50" : "#4CAF50", // Green for active
        theme.palette.mode === "dark" ? "#757575" : "#9E9E9E", // Gray for inactive
      ],
      "line-width": 2,
      "line-opacity": 0.8,
    }),
    [theme.palette.mode]
  );

  // Determine if clustering should be enabled
  const shouldCluster = filteredDevices.length > CLUSTER_THRESHOLD;

  // Create GeoJSON source for clustered devices
  const devicesGeoJson = React.useMemo(
    () => ({
      type: "FeatureCollection",
      features: filteredDevices
        .filter((device) => isValidCoordinate(device?.lng, device?.lat))
        .map((device) => ({
          type: "Feature",
          properties: {
            id: device.id,
            name: device.name,
            last_heard: device.last_heard,
            last_location: device.last_location,
            isOutdated: isDeviceOutdated(getDeviceFreshnessTime(device)),
          },
          geometry: {
            type: "Point",
            coordinates: [parseFloat(device.lng), parseFloat(device.lat)],
          },
        })),
    }),
    [filteredDevices, isDeviceOutdated]
  );

  const selectedCoords = React.useMemo(() => {
    if (
      !selectedDevice ||
      !isValidCoordinate(selectedDevice.lng, selectedDevice.lat)
    ) {
      return null;
    }

    return {
      longitude: Number(selectedDevice.lng),
      latitude: Number(selectedDevice.lat),
    };
  }, [selectedDevice]);

  // Handle cluster click - zoom into the cluster
  const handleClusterClick = React.useCallback(
    (e) => {
      const features = e.features;
      if (!features || !features.length) return;

      const feature = features[0];
      const clusterId = feature.properties.cluster_id;

      if (!clusterId) {
        // It's a single point, not a cluster
        const deviceId = feature.properties.id;
        const device = filteredDevices.find((d) => d.id === deviceId);
        if (device) {
          flyToDevice(device);
        }
        return;
      }

      // Get the cluster expansion zoom
      const map = getMapboxMap(mapRef);
      const mapboxSource = map?.getSource("devices-clustered");
      if (map && mapboxSource) {
        mapboxSource.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;

          map.flyTo({
            center: feature.geometry.coordinates,
            zoom: zoom,
            duration: 500,
          });
        });
      }
    },
    [filteredDevices, flyToDevice]
  );

  // Add click handlers for cluster layers
  React.useEffect(() => {
    const map = getMapboxMap(mapRef);
    if (!mapLoaded || !map || !shouldCluster) return;

    const onClusterClick = (e) => handleClusterClick(e);
    const onClustersMouseEnter = () => {
      map.getCanvas().style.cursor = "pointer";
    };
    const onClustersMouseLeave = () => {
      map.getCanvas().style.cursor = "";
    };
    const onUnclusteredMouseEnter = () => {
      map.getCanvas().style.cursor = "pointer";
    };
    const onUnclusteredMouseLeave = () => {
      map.getCanvas().style.cursor = "";
    };

    map.on("click", "clusters", onClusterClick);
    map.on("click", "unclustered-point", onClusterClick);

    // Change cursor on hover
    map.on("mouseenter", "clusters", onClustersMouseEnter);
    map.on("mouseleave", "clusters", onClustersMouseLeave);
    map.on("mouseenter", "unclustered-point", onUnclusteredMouseEnter);
    map.on("mouseleave", "unclustered-point", onUnclusteredMouseLeave);

    return () => {
      map.off("click", "clusters", onClusterClick);
      map.off("click", "unclustered-point", onClusterClick);
      map.off("mouseenter", "clusters", onClustersMouseEnter);
      map.off("mouseleave", "clusters", onClustersMouseLeave);
      map.off("mouseenter", "unclustered-point", onUnclusteredMouseEnter);
      map.off("mouseleave", "unclustered-point", onUnclusteredMouseLeave);
    };
  }, [mapLoaded, shouldCluster, handleClusterClick]);

  return (
    <>
      <Map
        mapboxAccessToken={CONFIG.mapboxAccessToken}
        mapStyle={mapStyle}
        onLoad={handleMapLoad}
        initialViewState={{ ...initialViewState, pitch }}
        id="map-panel"
      >
        {/* Navigation control is added programmatically in handleMapLoad */}
        <FullscreenControl position="top-left" />
        <ScaleControl position="bottom-right" />

        {/* Geofence layers - bottom layer */}
        <Source id="geofences" type="geojson" data={geofenceSource} />
        <Source id="geofence-pins" type="geojson" data={geofencePinSource} />

        <Layer
          id="geofence-fill"
          type="fill"
          source="geofences"
          paint={geofenceFillPaint}
        />

        <Layer
          id="geofence-outline"
          type="line"
          source="geofences"
          paint={geofenceStrokePaint}
        />

        {/* Geofence pins */}
        <Layer
          id="geofence-pins"
          type="circle"
          source="geofence-pins"
          paint={{
            "circle-radius": 8,
            "circle-color": [
              "case",
              ["==", ["get", "geofence_status"], "active"],
              theme.palette.mode === "dark" ? "#4CAF50" : "#4CAF50", // Green for active
              theme.palette.mode === "dark" ? "#757575" : "#9E9E9E", // Gray for inactive
            ],
            "circle-opacity": 0.9,
            "circle-stroke-width": 2,
            "circle-stroke-color": "#ffffff",
          }}
        />

        {/* Device markers - use clustering when there are many devices */}
        {shouldCluster ? (
          <>
            <Source
              id="devices-clustered"
              type="geojson"
              data={devicesGeoJson}
              cluster={true}
              clusterMaxZoom={14}
              clusterRadius={50}
            />

            {/* Cluster circles */}
            <Layer
              id="clusters"
              type="circle"
              source="devices-clustered"
              filter={["has", "point_count"]}
              paint={{
                "circle-color": [
                  "step",
                  ["get", "point_count"],
                  theme.palette.mode === "dark" ? "#4caf50" : "#4caf50", // < 10 devices
                  10,
                  theme.palette.mode === "dark" ? "#ff9800" : "#ff9800", // 10-50 devices
                  50,
                  theme.palette.mode === "dark" ? "#f44336" : "#f44336", // 50+ devices
                ],
                "circle-radius": [
                  "step",
                  ["get", "point_count"],
                  20, // < 10 devices
                  10,
                  25, // 10-50 devices
                  50,
                  30, // 50+ devices
                ],
                "circle-stroke-width": 2,
                "circle-stroke-color": "#ffffff",
              }}
            />

            {/* Cluster count labels */}
            <Layer
              id="cluster-count"
              type="symbol"
              source="devices-clustered"
              filter={["has", "point_count"]}
              layout={{
                "text-field": "{point_count_abbreviated}",
                "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
                "text-size": 12,
              }}
              paint={{
                "text-color": "#ffffff",
              }}
            />

            {/* Unclustered individual points */}
            <Layer
              id="unclustered-point"
              type="circle"
              source="devices-clustered"
              filter={["!", ["has", "point_count"]]}
              paint={{
                "circle-color": [
                  "case",
                  ["get", "isOutdated"],
                  "#f44336", // Red for outdated
                  "#4caf50", // Green for active
                ],
                "circle-radius": 8,
                "circle-stroke-width": 2,
                "circle-stroke-color": "#ffffff",
              }}
            />
          </>
        ) : (
          <VirtualizedDeviceMarkers
            devices={filteredDevices}
            mapRef={mapRef}
            mapLoaded={mapLoaded}
            isDeviceOutdated={isDeviceOutdated}
            onDeviceClick={flyToDevice}
            maxMarkers={1000}
          />
        )}

        {showPopup && selectedDevice && selectedCoords && (
          <Popup
            anchor="bottom"
            latitude={selectedCoords.latitude}
            longitude={selectedCoords.longitude}
            onClose={() => setShowPopup(false)}
            closeOnClick={false}
            closeButton={true}
            maxWidth="320px"
            offset={[0, -10]}
          >
            <>
              <div dangerouslySetInnerHTML={{ __html: popupContent }} />
              <Box sx={{ mt: 1.5, px: 1.5 }}>
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  endIcon={<ChevronRight />}
                  onClick={() => navigate(`/device/${selectedDevice.id}`)}
                  sx={{
                    borderRadius: 1.5,
                    textTransform: "none",
                    fontWeight: 600,
                    borderColor: alpha(theme.palette.primary.main, 0.5),
                    color: theme.palette.primary.main,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.primary.main, 0.16)
                        : "transparent",
                    "&:hover": {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: alpha(
                        theme.palette.primary.main,
                        theme.palette.mode === "dark" ? 0.25 : 0.12
                      ),
                    },
                  }}
                >
                  {t("common.view_device", { defaultValue: "View Device" })}
                </Button>
              </Box>
            </>
          </Popup>
        )}
      </Map>
    </>
  );
});

// Add display name for debugging
DevicesMap.displayName = "DevicesMap";

export default withMapErrorBoundary(DevicesMap);
